/**
 * Centralized pricing service for consistent formatting across pre-release project components
 */

export interface PricingDisplay {
  originalPrice: string;
  earlyBirdPrice?: string;
  savings?: string;
  displayFormat: 'single' | 'comparison';
}

export interface PreReleaseProject {
  proposed_price?: number;
  early_bird_discount?: number;
}

export class PricingService {
  /**
   * Format a price value with consistent currency display
   */
  static formatPrice(price?: number): string {
    if (price === undefined || price === null || price < 0) return 'TBD';
    return `$${price.toFixed(2)}`;
  }

  /**
   * Calculate early bird price from original price and discount percentage
   */
  static calculateEarlyBirdPrice(originalPrice: number, discountPercent: number): number {
    if (!originalPrice || !discountPercent || discountPercent <= 0) return originalPrice;
    return originalPrice * (1 - discountPercent / 100);
  }

  /**
   * Calculate early bird price from original price and discount amount (legacy support)
   */
  static calculateEarlyBirdPriceFromAmount(originalPrice: number, discountAmount: number): number {
    if (!originalPrice || !discountAmount || discountAmount <= 0) return originalPrice;
    return Math.max(0, originalPrice - discountAmount);
  }

  /**
   * Generate comprehensive pricing display for a project
   */
  static generatePricingDisplay(project: PreReleaseProject): PricingDisplay {
    const originalPrice = project.proposed_price || 0;
    
    if (!originalPrice) {
      return {
        originalPrice: 'TBD',
        displayFormat: 'single'
      };
    }

    // Handle early bird discount
    if (project.early_bird_discount && project.early_bird_discount > 0) {
      // Determine if discount is percentage or amount based on value
      // If discount is <= 100, treat as percentage (most common case)
      // If discount is > 100 and < original price, treat as amount (legacy format)
      // If discount is > original price, treat as percentage (edge case like 150% discount)
      let earlyBirdPrice: number;
      let savings: number;

      if (project.early_bird_discount <= 100) {
        // Treat as discount percentage (0-100%)
        earlyBirdPrice = this.calculateEarlyBirdPrice(originalPrice, project.early_bird_discount);
        savings = originalPrice - earlyBirdPrice;
      } else if (project.early_bird_discount < originalPrice) {
        // Treat as discount amount (legacy format) - only if > 100 and < original price
        earlyBirdPrice = this.calculateEarlyBirdPriceFromAmount(originalPrice, project.early_bird_discount);
        savings = project.early_bird_discount;
      } else {
        // Treat as discount percentage (edge case for very high percentages)
        earlyBirdPrice = this.calculateEarlyBirdPrice(originalPrice, project.early_bird_discount);
        savings = originalPrice - earlyBirdPrice;
      }

      return {
        originalPrice: this.formatPrice(originalPrice),
        earlyBirdPrice: this.formatPrice(earlyBirdPrice),
        savings: this.formatPrice(savings),
        displayFormat: 'comparison'
      };
    }

    return {
      originalPrice: this.formatPrice(originalPrice),
      displayFormat: 'single'
    };
  }

  /**
   * Generate formatted pricing text for display in cards and lists
   */
  static getCardPricingText(project: PreReleaseProject): string {
    const display = this.generatePricingDisplay(project);
    
    if (display.displayFormat === 'comparison' && display.earlyBirdPrice) {
      return `${display.earlyBirdPrice} (was ${display.originalPrice})`;
    }
    
    return display.originalPrice;
  }

  /**
   * Generate formatted pricing text for detailed views
   * @param project - The project with pricing information
   * @param t - Optional translation function for i18n support
   */
  static getDetailedPricingText(
    project: PreReleaseProject,
    t?: (key: string, params?: any) => string
  ): {
    originalText: string;
    earlyBirdText?: string;
    savingsText?: string;
    savingsAmount?: string;
  } {
    const display = this.generatePricingDisplay(project);
    
    const originalLabel = t ? t('preReleaseDetail.expectedPrice') : 'Original:';
    const result = {
      originalText: `${originalLabel} ${display.originalPrice}`
    };

    if (display.displayFormat === 'comparison' && display.earlyBirdPrice && display.savings) {
      const savingsAmount = display.savings;
      const earlyBirdLabel = t ? t('preReleaseDetail.earlyBirdPrice') : 'Early Bird:';
      return {
        ...result,
        earlyBirdText: `${earlyBirdLabel} ${display.earlyBirdPrice}`,
        savingsText: t 
          ? t('preReleaseDetail.saveMoney', { amount: savingsAmount })
          : `Save ${savingsAmount} with early bird pricing!`,
        savingsAmount: savingsAmount
      };
    }

    return result;
  }

  /**
   * Get just the price value without currency symbol for calculations
   */
  static getPriceValue(project: PreReleaseProject, useEarlyBird: boolean = false): number {
    if (!project.proposed_price) return 0;
    
    if (useEarlyBird && project.early_bird_discount) {
      const display = this.generatePricingDisplay(project);
      if (display.earlyBirdPrice && display.earlyBirdPrice !== 'TBD') {
        return parseFloat(display.earlyBirdPrice.replace('$', ''));
      }
    }
    
    return project.proposed_price;
  }

  /**
   * Validate pricing inputs
   */
  static validatePricing(proposedPrice?: number, earlyBirdDiscount?: number): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (proposedPrice !== undefined) {
      if (proposedPrice <= 0) {
        errors.push('Proposed price must be greater than 0');
      }
    }

    if (earlyBirdDiscount !== undefined && earlyBirdDiscount > 0 && proposedPrice) {
      // Check validation based on our discount interpretation logic
      if (earlyBirdDiscount <= 100) {
        // Treated as percentage - validate percentage range
        if (earlyBirdDiscount > 100) {
          errors.push('Early bird discount percentage cannot exceed 100%');
        }
      } else if (earlyBirdDiscount < proposedPrice) {
        // Treated as amount - validate amount is not too large
        // This is valid
      } else {
        // Amount that's too large or percentage > 100%
        if (earlyBirdDiscount >= proposedPrice) {
          errors.push('Early bird discount amount cannot be greater than or equal to the proposed price');
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

// Export default instance for convenience
export const pricingService = new PricingService();